import type { Vec2 } from '../../geo/types';
import type { PolygonIndex } from '../../geo/polygonIndex';
import { pointInRing, ringBounds, segmentIntersection } from '../../geo/polygon';
import { obbOverlap } from '../../geo/sat';
import type { Lot } from './types';

/** All vertices and edges must stay inside the buildable ground, including around its holes. */
export function footprintInside(index: PolygonIndex, ring: readonly Vec2[]): boolean {
  if (!ring.every((p) => index.contains(p))) return false;
  return !index.edgesNear(ringBounds(ring)).some((edge) =>
    pointInRing(edge.a, ring) || pointInRing(edge.b, ring) ||
    ring.some((a, i) => segmentIntersection(edge, { a, b: ring[(i + 1) % ring.length]! }))
  );
}

/** Convex lot footprints; enclosing rectangles may overlap beside an angled party wall. */
export function lotsOverlap(a: Lot, b: Lot, tolerance: number): boolean {
  if (!a.plot && !b.plot) return obbOverlap(a.obb, b.obb, tolerance);
  for (const ring of [a.corners, b.corners]) {
    for (let i = 0; i < ring.length; i++) {
      const p = ring[i]!, q = ring[(i + 1) % ring.length]!;
      const length = Math.hypot(q[0] - p[0], q[1] - p[1]);
      const axis: Vec2 = [-(q[1] - p[1]) / length, (q[0] - p[0]) / length];
      const span = (r: readonly Vec2[]) => {
        const values = r.map((v) => v[0] * axis[0] + v[1] * axis[1]);
        return { min: Math.min(...values), max: Math.max(...values) };
      };
      const x = span(a.corners), y = span(b.corners);
      if (x.max - tolerance <= y.min || y.max - tolerance <= x.min) return false;
    }
  }
  return true;
}

/** Front and back stay parallel; side walls follow the block's estimated plot axis. */
export function obliqueCorners(
  front: Vec2, facing: Vec2, width: number, depth: number, shear: number
): readonly [Vec2, Vec2, Vec2, Vec2] {
  const right: Vec2 = [-facing[1], facing[0]];
  const a: Vec2 = [front[0] - right[0] * width / 2, front[1] - right[1] * width / 2];
  const b: Vec2 = [front[0] + right[0] * width / 2, front[1] + right[1] * width / 2];
  const rear = (p: Vec2): Vec2 => [
    p[0] - facing[0] * depth + right[0] * shear * depth,
    p[1] - facing[1] * depth + right[1] * shear * depth,
  ];
  return [a, b, rear(b), rear(a)];
}
